import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';

const IcoScreen: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const IcoCheck: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ActivatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((s: RootState) => s.auth.value);
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!auth) {
      const codeParam = searchParams.get('code') || '';
      navigate(`/login?next=/activate${codeParam ? `?code=${codeParam}` : ''}`, { replace: true });
      return;
    }
    const c = searchParams.get('code');
    if (c) setCode(c.replace(/\D/g, '').slice(0, 9));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const handleActivate = async () => {
    if (code.length !== 9) { setError('Code must be exactly 9 digits'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post('/screens/activate', { activation_code: code });
      setSuccess(true);
      setTimeout(() => navigate('/screens'), 2000);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: 'var(--app-h)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--body-gradient)',
      px: '16px',
      py: '24px',
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: 420,
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--glass-border)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,.4)',
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: '12px',
          px: '24px', py: '20px',
          borderBottom: '1px solid var(--glass-border)',
          background: 'var(--glass-hover)',
        }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(var(--accent-rgb,99,102,241),.3), rgba(var(--accent-rgb,99,102,241),.12))',
            border: '1px solid var(--glass-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent)',
          }}>
            <IcoScreen />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
              {t('screens.activateTitle')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', mt: '2px' }}>
              PromoVideo
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: '24px', pt: '24px', pb: '28px' }}>
          {success ? (
            <Box sx={{ textAlign: 'center', py: '16px' }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: '16px',
                background: 'rgba(52,211,153,.15)',
                border: '1px solid rgba(52,211,153,.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#34d399',
              }}>
                <IcoCheck />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', mb: '6px' }}>
                Screen activated!
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Redirecting to screens…
              </Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 14, color: 'var(--text-secondary)', mb: '24px', lineHeight: 1.6 }}>
                {t('screens.activateDesc')}
              </Typography>

              {/* Code input */}
              <Box
                component="input"
                value={code}
                autoFocus
                maxLength={9}
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setCode(v);
                  setError('');
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' && code.length === 9) handleActivate();
                }}
                placeholder="000 000 000"
                sx={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'var(--input-bg)',
                  border: error ? '1px solid rgba(248,113,113,.6)' : '1px solid var(--input-border)',
                  borderRadius: '12px', px: '20px', py: '18px',
                  fontSize: 28, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
                  color: 'var(--text-primary)', letterSpacing: '8px', textAlign: 'center',
                  outline: 'none',
                  '&:focus': { borderColor: error ? 'rgba(248,113,113,.8)' : 'var(--accent)' },
                  '&::placeholder': { color: 'var(--text-disabled)', letterSpacing: '4px', fontWeight: 400, fontSize: 22 },
                  transition: 'border-color .15s',
                  mb: '12px',
                }}
              />

              {/* Progress dots */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '6px', mb: '20px' }}>
                {Array.from({ length: 9 }).map((_, i) => (
                  <Box key={i} sx={{
                    width: 7, height: 7, borderRadius: '50%',
                    bgcolor: i < code.length ? 'var(--accent)' : 'rgba(255,255,255,.12)',
                    transition: 'background-color .1s',
                  }} />
                ))}
              </Box>

              {/* Error */}
              {error && (
                <Box sx={{ mb: '20px', px: '14px', py: '11px', borderRadius: '10px', bgcolor: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.25)' }}>
                  <Typography sx={{ fontSize: 13, color: '#f87171' }}>{error}</Typography>
                </Box>
              )}

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box
                  component="button"
                  onClick={handleActivate}
                  disabled={loading || code.length !== 9}
                  sx={{
                    width: '100%', py: '14px', borderRadius: '12px',
                    cursor: loading || code.length !== 9 ? 'default' : 'pointer',
                    background: 'linear-gradient(135deg, var(--accent), var(--accent-light))',
                    border: 'none',
                    color: '#fff', fontSize: 15, fontWeight: 600,
                    opacity: loading || code.length !== 9 ? .5 : 1,
                    transition: 'opacity .15s',
                    boxShadow: '0 2px 12px var(--accent-glow)',
                  }}
                >
                  {loading ? '…' : t('screens.activateBtn')}
                </Box>
                <Box
                  component="button"
                  onClick={() => navigate('/screens')}
                  sx={{
                    width: '100%', py: '12px', borderRadius: '12px', cursor: 'pointer',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--text-secondary)', fontSize: 14, fontWeight: 500,
                    '&:hover': { color: 'var(--text-primary)' },
                    transition: 'color .12s',
                  }}
                >
                  {t('common.cancel')}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ActivatePage;
